/**
 * Enterprise / Agents Page
 *
 * Nexus business positioning with homepage-style open layout
 * (icon rails, copy+visual — no feature card grids).
 *
 * Primary responsibilities:
 * - Position Losi Nexus for business buyers
 * - Present plans and AI readiness together
 *
 * Dependencies:
 * - Nexus pricing
 * - Hero video
 * - marketing primitives
 */

'use client'

import { cn } from '@/lib/utils'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { ArrowRightIcon } from '@radix-ui/react-icons'
import {
  IconHeadset,
  IconMailForward,
  IconMessages,
  IconPhone
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

import { HeroVideo } from '@/components/marketing/hero-video'
import { GeneratedMarketingAsset } from '@/components/marketing/generated-marketing-asset'
import {
  MarketingAssetOuterFade,
  marketingAssetSoftEdgeClassName
} from '@/components/marketing/marketing-asset-outer-fade'
import { CultTextureButton } from '@/components/marketing/cult-ui-components'
import {
  MarketingContainer,
  MarketingPage,
  MarketingRailIcon
} from '@/components/marketing/marketing-primitives'
import { AiReadinessLeadMagnetModal } from '@/components/quick-ai-readiness/lead-magnet-modal'
import { AiReadinessLeadMagnetPrompt } from '@/components/quick-ai-readiness/lead-magnet-prompt'
import { getMarketingCopy } from '@/lib/marketing-copy'

const benefitIcons = [IconPhone, IconMessages, IconMailForward, IconHeadset] as const

const NexusPricing = dynamic(() => import('@/components/nexus/nexus-pricing'), {
  loading: () => (
    <div className="min-h-[420px] py-8">
      <div className="h-8 w-40 rounded-full bg-slate-200/80 dark:bg-white/10" />
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="min-h-[240px] border-t border-slate-200/80 pt-6 dark:border-white/10"
          />
        ))}
      </div>
    </div>
  ),
  ssr: false
})

export default function EnterprisePage() {
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)

  return (
    <MarketingPage>
      <MarketingContainer>
        {/* Hero */}
        <section className="relative grid items-center gap-10 py-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:py-10">
          <div className="flex max-w-[40rem] flex-col gap-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {copy.enterprise.eyebrow}
            </p>
            <h1 className="font-display text-balance text-5xl font-medium leading-[0.96] tracking-[-0.02em] text-slate-950 sm:text-6xl dark:text-slate-50">
              {copy.enterprise.title}
            </h1>
            <p className="text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
              {copy.enterprise.description}
            </p>
            <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:flex-wrap">
              <CultTextureButton asChild>
                <Link href="#pricing">
                  {copy.common.seePlans}
                  <ArrowRightIcon className="ml-2 size-4" />
                </Link>
              </CultTextureButton>
              <AiReadinessLeadMagnetModal source="enterprise_page">
                <CultTextureButton tone="light">{copy.common.checkAiReadiness}</CultTextureButton>
              </AiReadinessLeadMagnetModal>
            </div>
          </div>
          <div className={cn("relative isolate min-h-[280px] overflow-hidden sm:min-h-[360px] lg:min-h-[420px]", marketingAssetSoftEdgeClassName)}>
            <HeroVideo className="absolute inset-0 h-full w-full object-cover" />
            <MarketingAssetOuterFade />
          </div>
        </section>

        {/* Why — coverage grid (calls / chat / follow-up / team) */}
        <section className="relative mt-10 py-12 lg:mt-14 lg:py-16">
          <div className="mx-auto max-w-[46rem] text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {copy.enterprise.why.eyebrow}
            </p>
            <h2 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {copy.enterprise.why.title}
            </h2>
            <p className="mx-auto mt-4 max-w-[40ch] text-base leading-8 text-slate-600 dark:text-slate-300">
              {copy.enterprise.why.description}
            </p>
          </div>
          <ol className="mt-12 grid gap-0 border-y border-slate-200/80 dark:border-white/10 sm:grid-cols-2">
            {copy.enterprise.benefitCards.map((card, index) => {
              const Icon = benefitIcons[index] || benefitIcons[0]

              return (
                <li
                  key={card.title}
                  className={`flex flex-col gap-3 px-5 py-8 ${
                    index % 2 === 1
                      ? 'sm:border-l sm:border-slate-200/80 dark:sm:border-white/10'
                      : ''
                  } ${
                    index >= 2
                      ? 'border-t border-slate-200/80 dark:border-white/10'
                      : index > 0
                        ? 'border-t border-slate-200/80 sm:border-t-0 dark:border-white/10'
                        : ''
                  }`}
                >
                  <MarketingRailIcon icon={Icon} />
                  <h3 className="text-xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                    {card.title}
                  </h3>
                  <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                    {card.description}
                  </p>
                </li>
              )
            })}
          </ol>
        </section>

        {/* Includes + visual */}
        <section className="relative mt-4 overflow-hidden py-12 lg:mt-8 lg:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14">
            <div className={cn("relative isolate min-h-[280px] overflow-hidden sm:min-h-[360px] lg:min-h-[400px]", marketingAssetSoftEdgeClassName)}>
              <GeneratedMarketingAsset
                assetKey="nexusHero"
                frame={false}
                presentation="open"
                className="absolute inset-0"
                imageClassName="object-contain object-center opacity-95"
                sizes="(min-width: 1024px) 560px, 100vw"
              />
              <MarketingAssetOuterFade />
            </div>
            <div className="flex max-w-[40rem] flex-col gap-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                {copy.enterprise.includes.eyebrow}
              </p>
              <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
                {copy.enterprise.includes.title}
              </h2>
              <p className="text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                {copy.enterprise.includes.description}
              </p>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                {copy.enterprise.includes.panelLabel}
              </p>
              <h3 className="text-2xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                {copy.enterprise.includes.panelTitle}
              </h3>
              <div className="mt-1 space-y-4 border-t border-slate-200/80 pt-5 dark:border-white/10">
                {copy.enterprise.includes.points.map(point => (
                  <p
                    key={point}
                    className="text-sm leading-7 text-slate-600 dark:text-slate-300"
                  >
                    {point}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* AI readiness — interactive form owns the section heading (avoid duplicate H2) */}
        <section className="relative mt-8 py-12 lg:mt-12 lg:py-16">
          <div className="mx-auto max-w-[40rem]">
            <AiReadinessLeadMagnetPrompt
              source="enterprise_page"
              variant="panel"
              eyebrow={copy.enterprise.includes.readinessLabel}
              title={copy.enterprise.includes.readinessTitle}
              description={copy.enterprise.includes.readinessDescription}
              items={copy.leadMagnet.afterSubmitSteps}
            />
          </div>
        </section>

        {/* Plans — pricing is interactive selection */}
        <section id="pricing" className="relative mt-8 scroll-mt-32 py-12 lg:mt-12 lg:py-16">
          <div className="mx-auto max-w-[46rem] text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {copy.enterprise.plans.eyebrow}
            </p>
            <h2 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {copy.enterprise.plans.title}
            </h2>
            <p className="mx-auto mt-4 max-w-[40rem] text-base leading-8 text-slate-600 dark:text-slate-300">
              {copy.enterprise.plans.description}
            </p>
          </div>
          <div className="mt-10">
            <NexusPricing marketingSectionVariant="ai_readiness" />
          </div>
        </section>
      </MarketingContainer>
    </MarketingPage>
  )
}
